import type { Team } from './domain.js';

// ── The REGISTER read model (« Registre », migration 0023/0024) ──────────────
//
// Moved out of apps/admin/src/logic.ts at ladder step 6. It is C3 domain, not
// an app concern: the shape is dictated by the staff_admin_list_employees RPC,
// and RegisterStore's contract is defined in terms of it, so it cannot live
// downstream of the port that returns it.
//
// It is a SEPARATE aggregate from RosterStore's Employee: that one is "who can
// be staffed today" (eight columns, staff-readable); this one is the personnel
// register — contract history plus RUP identity (SSN, address, birth date),
// operator-only and legally sensitive. Merging them would put RUP columns
// behind a port every SPA holds.

/** A single employment period (staff_contracts row → camelCase). */
export interface ContractPeriod {
  id: string;
  contractType: string | null;
  contractNumber: string | null; // « Numéro de Contrat » (DSN)
  startDate: string; // 'YYYY-MM-DD'
  endDate: string | null; // null = ongoing (CDI or not yet ended)
  hireRank: number | null; // RUP numéro d'ordre — auto-assigned, not editable (0027)
  note: string | null;
  changeDate: string | null; // last modification date (0027)
  // RUP per-hiring columns (migration 0024)
  emploi: string | null;
  qualification: string | null;
  pcsEse: string | null;
  statutConventionnel: string | null;
  niveau: string | null;
  echelon: string | null;
  coef: string | null;
  brutHoraire: number | null;
  brutMensuel: number | null;
  /** Contractual weekly hours (35 = full time). Null = unknown; the staffing allocator then has no hours signal for this person. */
  weeklyHours: number | null;
  tempsPartiel: boolean;
  /** Descriptive only — carries no locking behaviour. `closedAt` is the source of truth for "terminated" (migration 20260811). */
  motifSortie: string | null;
  /** Effective closure date — the "clôturer" act. NULL = not yet formally closed; this is what "terminated" means, not motifSortie. */
  closedAt: string | null;
  authEntree: string | null;
  authSortie: string | null;
  docType: string | null;
  docNumber: string | null;
  apprenti: boolean;
  contratQualification: boolean;
  contratAdaptation: boolean;
}

/** The full employee record as returned by staff_admin_list_employees (0023). */
export interface AdminEmployee {
  id: string;
  employeeId: string;
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  team: '' | Team;
  isSenior: boolean;
  hasPin: boolean;
  isLinked: boolean;
  isCurrent: boolean; // has a period covering today
  firstStartDate: string | null; // earliest contract start — the seniority key
  contractStartDate: string | null; // cache: current/latest period
  contractEndDate: string | null;
  // RUP person identity (migration 0024)
  birthName: string | null;
  address: string | null;
  ssn: string | null;
  nationality: string | null;
  birthDate: string | null;
  sex: string | null;
  /** Optional short recognition name (chat, Planning/Pointage) — unique, case-insensitively, across all employees past and current. */
  nickname: string | null;
  contracts: ContractPeriod[];
}

/** Raw RPC row (snake_case + jsonb contracts). */
export interface AdminEmployeeRaw {
  id: string;
  employee_id: string;
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  team: Team | null;
  is_senior: boolean;
  contract_start_date: string | null;
  contract_end_date: string | null;
  has_pin: boolean;
  is_linked: boolean;
  is_current: boolean;
  first_start_date: string | null;
  birth_name: string | null;
  address: string | null;
  ssn: string | null;
  nationality: string | null;
  birth_date: string | null;
  sex: string | null;
  nickname: string | null;
  contracts: RawContract[] | null;
}

/** One contract object inside the RPC's jsonb (camelCase keys per the SQL). */
export interface RawContract {
  id: string;
  contractType: string | null;
  contractNumber: string | null;
  startDate: string;
  endDate: string | null;
  hireRank: number | null;
  note: string | null;
  changeDate: string | null;
  emploi: string | null;
  qualification: string | null;
  pcsEse: string | null;
  statutConventionnel: string | null;
  niveau: string | null;
  echelon: string | null;
  coef: string | null;
  brutHoraire: number | null;
  brutMensuel: number | null;
  /** Contractual weekly hours (35 = full time). Null = unknown; the staffing allocator then has no hours signal for this person. */
  /** Optional on the WIRE: absent from a function version that predates the weekly-hours migration. */
  weeklyHours?: number | null;
  tempsPartiel: boolean;
  motifSortie: string | null;
  /** Optional on the WIRE: absent from a function version that predates the close migration (20260811). */
  closedAt?: string | null;
  authEntree: string | null;
  authSortie: string | null;
  docType: string | null;
  docNumber: string | null;
  apprenti: boolean;
  contratQualification: boolean;
  contratAdaptation: boolean;
}

export function rowToAdminEmployee(r: AdminEmployeeRaw): AdminEmployee {
  return {
    id: r.id,
    employeeId: r.employee_id,
    lastName: r.last_name,
    firstName: r.first_name,
    email: r.email,
    phone: r.phone,
    team: r.team ?? '',
    isSenior: r.is_senior,
    hasPin: r.has_pin,
    isLinked: r.is_linked,
    isCurrent: r.is_current,
    firstStartDate: r.first_start_date,
    contractStartDate: r.contract_start_date,
    contractEndDate: r.contract_end_date,
    birthName: r.birth_name,
    address: r.address,
    ssn: r.ssn,
    nationality: r.nationality,
    birthDate: r.birth_date,
    sex: r.sex,
    nickname: r.nickname,
    contracts: (r.contracts ?? []).map((c) => ({
      id: c.id,
      contractType: c.contractType,
      contractNumber: c.contractNumber,
      startDate: c.startDate,
      endDate: c.endDate,
      hireRank: c.hireRank,
      note: c.note,
      changeDate: c.changeDate,
      emploi: c.emploi,
      qualification: c.qualification,
      pcsEse: c.pcsEse,
      statutConventionnel: c.statutConventionnel,
      niveau: c.niveau,
      echelon: c.echelon,
      coef: c.coef,
      brutHoraire: c.brutHoraire,
      brutMensuel: c.brutMensuel,
      weeklyHours: c.weeklyHours ?? null,
      tempsPartiel: c.tempsPartiel,
      motifSortie: c.motifSortie,
      closedAt: c.closedAt ?? null,
      authEntree: c.authEntree,
      authSortie: c.authSortie,
      docType: c.docType,
      docNumber: c.docNumber,
      apprenti: c.apprenti,
      contratQualification: c.contratQualification,
      contratAdaptation: c.contratAdaptation,
    })),
  };
}
